import { createTheme, type Theme } from "@mui/material/styles";

// The people accent: contact cards and the contact drawer they open share it.
interface PeopleColor {
  /** Ring and hover border. */
  main: string;
  /** The card's and the drawer header's background. */
  light: string;
  /** Text on `light`. */
  dark: string;
  /** The drawer header's hairline. */
  border: string;
}

declare module "@mui/material/styles" {
  interface Palette {
    people: PeopleColor;
    /** Quiet grey controls: `<Button color="neutral">` (Clear all, Filter). */
    neutral: Palette["primary"];
  }
  interface PaletteOptions {
    people?: PeopleColor;
    neutral?: PaletteOptions["primary"];
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    neutral: true;
  }
}

declare module "@mui/material/IconButton" {
  interface IconButtonPropsColorOverrides {
    neutral: true;
  }
}

// A switch's track (w×h, corner radius) and the thumb sliding 2px inside it,
// its corners concentric with the track's.
function switchSize(w: number, h: number, thumb: number, radius: number) {
  return {
    width: w,
    height: h,
    "& .MuiSwitch-switchBase": { padding: 0 },
    "& .MuiSwitch-switchBase.Mui-checked": { transform: `translateX(${w - thumb - 4}px)` },
    "& .MuiSwitch-thumb": { width: thumb, height: thumb, borderRadius: radius - 2 },
    "& .MuiSwitch-track": { borderRadius: radius },
  };
}

// Controls (buttons, inputs, chips, toggle options) share 6px corners and a
// 32px height; the boxes they sit in take 8px, dialogs 10px.
type Tone = "primary" | "secondary" | "success" | "warning" | "error" | "info";
const TONES: readonly string[] = ["primary", "secondary", "success", "warning", "error", "info"];
const isTone = (color: unknown): color is Tone => TONES.includes(color as string);
// The theme handed to style overrides: CSS variables are on.
type VarsTheme = Theme & { vars: NonNullable<Theme["vars"]> };

// A soft tint of a palette colour, and text in that colour that stays readable on it.
function tint(theme: VarsTheme, color: Tone, percent: number) {
  return `color-mix(in srgb, ${theme.vars.palette[color].main} ${percent}%, transparent)`;
}
function ink(theme: VarsTheme, color: Tone) {
  return `color-mix(in srgb, ${theme.vars.palette[color].main} 72%, ${theme.vars.palette.text.primary})`;
}

// Surfaces that paint their own shade; a content box inside one takes the other.
const SURFACE = [".MuiPaper-outlined", ".MuiAccordion-root", ".MuiDialog-paper", ".MuiDrawer-paper"];

// Keyboard focus on a control: a 2px primary ring, just outside it by default.
function focusRing(theme: VarsTheme, offset = 2) {
  return { outline: `2px solid ${theme.vars.palette.primary.main}`, outlineOffset: offset };
}

/**
 * What floats over the page: menus, popovers, select and autocomplete lists
 * and the sidebar's flyouts. White in light mode, lifted off the background in
 * dark, a soft shadow and no border. Themed on `MuiPopover`; the sidebar's
 * `Popper` flyouts use it directly.
 */
export function floatingSurface(t: Theme) {
  const theme = t as VarsTheme;
  return {
    backgroundColor: theme.vars.palette.background.default,
    backgroundImage: "none",
    // A string: in `sx` (the sidebar's flyouts) a number is shape × n.
    borderRadius: "8px",
    boxShadow:
      "0 12px 32px rgba(var(--mui-palette-text-primaryChannel) / 0.12), 0 2px 6px rgba(var(--mui-palette-text-primaryChannel) / 0.06)",
    ...theme.applyStyles("dark", {
      backgroundColor: theme.vars.palette.background.paper,
      boxShadow:
        "0 12px 32px rgba(var(--mui-palette-common-backgroundChannel) / 0.5), 0 2px 6px rgba(var(--mui-palette-common-backgroundChannel) / 0.3)",
    }),
  };
}

/** A row in anything floating: a 32px pill inset 4px from the edges. */
function menuRow(theme: VarsTheme) {
  const selected = (percent: number) => `rgba(${theme.vars.palette.primary.mainChannel} / ${percent})`;
  return {
    margin: "0 4px",
    padding: "4px 10px",
    // Doubled: MenuItem's own `minHeight: auto` above `sm` outranks a plain rule.
    "&&": { minHeight: 32 },
    gap: 10,
    borderRadius: 4,
    fontSize: "0.8125rem",
    // `Mui-focused`: the row a combobox's arrow keys are on (focus stays in its field).
    "&:hover, &.Mui-focused": { backgroundColor: theme.vars.palette.action.hover },
    "&.Mui-focusVisible": { backgroundColor: "transparent", ...focusRing(theme, -2) },
    "&.Mui-selected": {
      backgroundColor: selected(0.1),
      color: theme.vars.palette.primary.main,
      "& .MuiListItemIcon-root": { color: "inherit" },
      "& .MuiListItemText-primary": { fontWeight: 600 },
      "&:hover, &.Mui-focusVisible": { backgroundColor: selected(0.15) },
    },
    "& .MuiListItemIcon-root": {
      minWidth: 0,
      justifyContent: "center",
      color: theme.vars.palette.text.secondary,
      "& > svg": { fontSize: 16 },
    },
    "& .MuiListItemText-root": { marginTop: 0, marginBottom: 0, minWidth: 0 },
  };
}

/** A section label over a group of rows (a menu's "Tenants", the sidebar's areas). */
const sectionLabelSx = {
  position: "static",
  backgroundColor: "transparent",
  lineHeight: "28px",
  fontSize: "0.6875rem",
  fontWeight: 600,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "var(--mui-palette-text-secondary)",
} as const;

const theme = createTheme({
  cssVariables: {
    colorSchemeSelector: 'data-mui-color-scheme',
  },
  colorSchemes: {
    light: {
      palette: {
        primary: {
          main: "#602df2",
          light: "#6d3ef3",
          dark: "#4a1fd4",
          contrastText: "#ffffff",
        },
        secondary: {
          main: "#70e0b1",
          dark: "#00302a",
        },
        error: {
          main: "#fb4e63",
          light: "#ffeaec",
        },
        warning: {
          main: "#ff974b",
          light: "#fff3e9",
        },
        success: {
          main: "#00bb9e",
          light: "#e0f7f3",
        },
        background: {
          default: "#ffffff",
          paper: "#f5f5f6",
        },
        text: {
          primary: "#0d0b13",
          secondary: "#6e6d71",
        },
        neutral: {
          main: "#6e6d71",
          light: "#8e8d91",
          dark: "#0d0b13",
          contrastText: "#ffffff",
        },
        divider: "#d8d8d9",
        people: {
          main: "#2fbf86",
          light: "#eaf8f1",
          dark: "#0b4a3a",
          border: "#cdeedf",
        },
      },
    },
    dark: {
      palette: {
        primary: {
          main: "#8b6cf5",
          light: "#a78bfa",
          dark: "#602df2",
          contrastText: "#ffffff",
        },
        secondary: {
          main: "#70e0b1",
          dark: "#00302a",
        },
        error: {
          main: "#fb4e63",
          light: "#3d1a20",
        },
        warning: {
          main: "#ff974b",
          light: "#3d2a15",
        },
        success: {
          main: "#00bb9e",
          light: "#0d2e28",
        },
        background: {
          default: "#121218",
          paper: "#1a1a24",
        },
        text: {
          primary: "#e8e6f0",
          secondary: "#9e9da3",
        },
        neutral: {
          main: "#9e9da3",
          light: "#b8b7bc",
          dark: "#e8e6f0",
          contrastText: "#121218",
        },
        divider: "#2e2e3a",
        people: {
          main: "#4fd6a0",
          light: "#14302a",
          dark: "#a6f0d1",
          border: "#1f4a3e",
        },
      },
    },
  },
  typography: {
    fontFamily: '"Inter", sans-serif',
    fontSize: 13,
    h1: { fontSize: "1.75rem", fontWeight: 700, lineHeight: 1.2 },
    h2: { fontSize: "1.5rem", fontWeight: 700, lineHeight: 1.25 },
    h3: { fontSize: "1.25rem", fontWeight: 600, lineHeight: 1.3 },
    h4: { fontSize: "1.1rem", fontWeight: 600, lineHeight: 1.35 },
    h5: { fontSize: "0.95rem", fontWeight: 600, lineHeight: 1.4 },
    h6: { fontSize: "0.875rem", fontWeight: 600, lineHeight: 1.4 },
    subtitle1: { fontSize: "0.8125rem", fontWeight: 500, lineHeight: 1.4 },
    subtitle2: { fontSize: "0.75rem", fontWeight: 500, lineHeight: 1.4 },
    body1: { fontSize: "0.8125rem", lineHeight: 1.5 },
    body2: { fontSize: "0.75rem", lineHeight: 1.5 },
    caption: { fontSize: "0.6875rem", lineHeight: 1.4 },
    overline: { fontSize: "0.625rem", lineHeight: 1.5, letterSpacing: "0.08em" },
    button: {
      fontSize: "0.8125rem",
      textTransform: "none",
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 4,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        :root {
          --mui-fontFamily-body: "Inter", sans-serif;
          --mui-fontFamily-code: "Inter", sans-serif;
        }
        html, body, *, *::before, *::after {
          font-family: "Inter", sans-serif;
        }
      `,
    },
    // Flat buttons. `outlined` is drawn as a soft tint of its colour with no
    // border, never the background of whatever is behind it.
    MuiButton: {
      defaultProps: {
        size: "small",
        disableElevation: true,
        disableRipple: true,
      },
      styleOverrides: {
        root: ({ theme, ownerState }) => {
          const tone = isTone(ownerState.color) ? ownerState.color : null;
          const outlined = ownerState.variant === "outlined";
          return {
            borderRadius: 6,
            minHeight: 32,
            "&.Mui-focusVisible": focusRing(theme),
            ...(outlined && {
              border: 0,
              ...(tone && { "--variant-outlinedColor": ink(theme, tone) }),
              "--variant-outlinedBg": tone ? tint(theme, tone, 12) : theme.vars.palette.action.hover,
              "&.Mui-disabled": { border: 0, backgroundColor: theme.vars.palette.action.disabledBackground },
            }),
            "&:hover": {
              ...(ownerState.variant === "contained" &&
                ownerState.color === "primary" && { backgroundColor: theme.vars.palette.primary.light }),
              ...(outlined && {
                border: 0,
                "--variant-outlinedBg": tone ? tint(theme, tone, 20) : theme.vars.palette.action.selected,
              }),
              // Grey until hovered.
              ...(ownerState.color === "neutral" && { color: theme.vars.palette.text.primary }),
            },
          };
        },
        sizeSmall: {
          padding: "4px 12px",
          fontSize: "0.8125rem",
        },
      },
    },
    MuiButtonGroup: {
      defaultProps: {
        size: "small",
        disableElevation: true,
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          // Borderless halves: a 1px gap instead of the overlapping border.
          "& .MuiButton-outlined:not(.MuiButtonGroup-firstButton)": { marginLeft: 1 },
        },
      },
    },
    // Rounded squares, grey until hovered.
    MuiIconButton: {
      defaultProps: {
        size: "small",
        disableRipple: true,
      },
      styleOverrides: {
        root: ({ theme, ownerState }) => ({
          borderRadius: 6,
          "&.Mui-focusVisible": focusRing(theme),
          ...(ownerState.color === "default" && {
            color: theme.vars.palette.text.secondary,
            "&:hover": { backgroundColor: theme.vars.palette.action.hover, color: theme.vars.palette.text.primary },
          }),
        }),
        sizeSmall: {
          padding: 4,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: "small",
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: "0.8125rem",
        },
        sizeSmall: {
          fontSize: "0.8125rem",
        },
      },
    },
    MuiOutlinedInput: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        root: {
          fontSize: "0.8125rem",
          borderRadius: 6,
          // 32px tall, like the buttons beside it (Autocomplete pads its own root).
          "&.MuiInputBase-sizeSmall:not(.MuiInputBase-multiline, .MuiAutocomplete-inputRoot) .MuiOutlinedInput-input": {
            paddingTop: 6.5,
            paddingBottom: 6.5,
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderWidth: 1,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderWidth: 1,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderWidth: 1,
          },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": {
            borderWidth: 1,
          },
        },
      },
    },
    MuiInputLabel: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        root: {
          fontSize: "0.8125rem",
          // Centred in the 32px input until it shrinks into the outline.
          "&.MuiInputLabel-outlined.MuiInputLabel-sizeSmall:not(.MuiInputLabel-shrink)": {
            transform: "translate(14px, 7px) scale(1)",
          },
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        select: {
          fontSize: "0.8125rem",
        },
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root.MuiInputBase-sizeSmall": { paddingTop: 4, paddingBottom: 4 },
        },
        paper: ({ theme }) => floatingSurface(theme),
        // Options are menu rows: highlighted (hover, arrow keys) in grey,
        // chosen in the primary tint.
        listbox: ({ theme }) => ({
          padding: "4px 0",
          fontSize: "0.8125rem",
          "& .MuiAutocomplete-option": {
            margin: "0 4px",
            padding: "4px 10px",
            minHeight: 32,
            borderRadius: 4,
            "&.Mui-focused": { backgroundColor: theme.vars.palette.action.hover },
            '&[aria-selected="true"]': {
              backgroundColor: `rgba(${theme.vars.palette.primary.mainChannel} / 0.1)`,
              color: theme.vars.palette.primary.main,
              "&.Mui-focused": { backgroundColor: `rgba(${theme.vars.palette.primary.mainChannel} / 0.15)` },
            },
          },
        }),
        tag: {
          margin: "1px 2px",
        },
      },
    },
    MuiFormControl: {
      defaultProps: {
        size: "small",
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: "0.6875rem",
          marginTop: 2,
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: "0.8125rem",
        },
      },
    },
    MuiChip: {
      defaultProps: {
        size: "small",
        variant: "filled",
      },
      styleOverrides: {
        // `outlined` is a soft tint of its colour, like an outlined button.
        root: ({ theme, ownerState }) => {
          const tone = isTone(ownerState.color) ? ownerState.color : null;
          return {
            fontSize: "0.75rem",
            height: 24,
            borderRadius: 6,
            ...(ownerState.variant === "outlined" && {
              borderColor: "transparent",
              backgroundColor: tone ? tint(theme, tone, 14) : theme.vars.palette.action.selected,
              color: tone ? ink(theme, tone) : theme.vars.palette.text.primary,
              "&.MuiChip-clickable:hover, &.Mui-focusVisible": {
                backgroundColor: tone ? tint(theme, tone, 22) : theme.vars.palette.action.focus,
              },
            }),
          };
        },
        sizeSmall: {
          height: 22,
        },
      },
    },
    // Content surfaces are filled, never outlined: a grey box on the white
    // page, and whatever sits inside a surface (a panel, a drawer, a dialog)
    // takes the other shade, so boxes in boxes alternate grey / white / grey.
    // Plain Paper keeps its elevated default: menus, popovers and dialogs
    // render one and need the shadow, so content Papers ask for
    // `variant="outlined"` (borderless here) themselves.
    MuiPaper: {
      styleOverrides: {
        outlined: ({ theme }) => ({
          borderRadius: 8,
          border: 0,
          backgroundColor: "var(--mui-palette-background-paper)",
          [SURFACE.map((s) => `${s} &`).join(", ")]: {
            backgroundColor: "var(--mui-palette-background-default)",
          },
          [SURFACE.map((s) => `${s} .MuiPaper-outlined &`).join(", ")]: {
            backgroundColor: "var(--mui-palette-background-paper)",
          },
          // A floating surface is white in light mode (a box in it stays
          // grey) and grey in dark (a box in it goes dark).
          ".MuiPopover-paper &": theme.applyStyles("dark", {
            backgroundColor: theme.vars.palette.background.default,
          }),
        }),
      },
    },
    MuiPopover: {
      defaultProps: {
        transitionDuration: 150,
      },
      styleOverrides: {
        paper: ({ theme }) => floatingSurface(theme),
      },
    },
    MuiMenu: {
      defaultProps: {
        transitionDuration: 150,
      },
    },
    // Menus and select lists: rows sit 4px in from the top and bottom too.
    MuiList: {
      styleOverrides: {
        root: {
          '&[role="menu"], &[role="listbox"]': {
            paddingTop: 4,
            paddingBottom: 4,
            "& .MuiListSubheader-root": { marginTop: 4 },
            "& > .MuiDivider-root": { marginTop: 4, marginBottom: 4 },
          },
        },
      },
    },
    MuiListSubheader: {
      styleOverrides: {
        root: { ...sectionLabelSx, paddingLeft: 14, paddingRight: 14 },
      },
    },
    MuiCard: {
      defaultProps: {
        variant: "outlined",
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiAccordion: {
      defaultProps: {
        disableGutters: true,
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          "&:before": { display: "none" },
          "&:first-of-type, &:last-of-type": { borderRadius: 8 },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
        },
        bar: {
          borderRadius: 4,
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: "12px 16px",
        },
        title: {
          fontSize: "0.875rem",
          fontWeight: 600,
        },
        subheader: {
          fontSize: "0.75rem",
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: "12px 16px",
          "&:last-child": {
            paddingBottom: 12,
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          fontSize: "0.8125rem",
          padding: "4px 12px",
          alignItems: "center",
        },
        icon: {
          fontSize: "1.125rem",
          padding: "4px 0",
          marginRight: 8,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: "0.8125rem",
          padding: "6px 12px",
        },
        head: ({ theme }) => ({
          fontSize: "0.75rem",
          fontWeight: 600,
          color: theme.vars.palette.text.secondary,
        }),
      },
    },
    // The last row's bottom border would double the container's outline.
    MuiTableBody: {
      styleOverrides: {
        root: {
          "& > .MuiTableRow-root:last-child > .MuiTableCell-root": {
            borderBottom: 0,
          },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          fontSize: "0.75rem",
        },
        selectLabel: {
          fontSize: "0.75rem",
        },
        displayedRows: {
          fontSize: "0.75rem",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontSize: "0.8125rem",
          minHeight: 40,
          padding: "6px 16px",
          textTransform: "none",
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 40,
        },
      },
    },
    // A compact, squared toggle: a 30×17 track (26×15 small) with a white
    // thumb, primary when on. No padding, so it lines up with text and fits
    // table rows.
    MuiSwitch: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          ...switchSize(30, 17, 13, 5),
          "&.MuiSwitch-sizeSmall": switchSize(26, 15, 11, 4.5),
          padding: 0,
          overflow: "visible",
          // FormControlLabel pulls its control 11px out to cancel the stock padding.
          ".MuiFormControlLabel-root > &": { marginLeft: 11, marginRight: 8 },
          ".MuiFormControlLabel-labelPlacementStart > &": { marginLeft: 8, marginRight: 11 },
        },
        switchBase: ({ theme }) => ({
          margin: 2,
          color: "#fff",
          transitionDuration: "180ms",
          "&:hover": { backgroundColor: "transparent" },
          "&.Mui-checked": {
            color: "#fff",
            "&:hover": { backgroundColor: "transparent" },
            "& + .MuiSwitch-track": { opacity: 1, backgroundColor: theme.vars.palette.primary.main },
          },
          "&.Mui-disabled, &.Mui-checked.Mui-disabled": { color: "#fff" },
          "&.Mui-disabled + .MuiSwitch-track": { opacity: 0.45 },
          "&.Mui-focusVisible + .MuiSwitch-track": {
            boxShadow: `0 0 0 2px ${theme.vars.palette.background.default}, 0 0 0 4px ${theme.vars.palette.primary.main}`,
          },
        }),
        thumb: {
          boxShadow: "none",
        },
        track: ({ theme }) => ({
          opacity: 1,
          backgroundColor: `color-mix(in srgb, ${theme.vars.palette.text.primary} 28%, transparent)`,
          transition: theme.transitions.create("background-color", { duration: 180 }),
        }),
      },
    },
    // Pick one of a few: the options sit in a grey tray, the chosen one raised
    // in a darker grey. Concentric corners: 8px tray, 2px inset, 6px options.
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.vars.palette.action.hover,
          borderRadius: 8,
          padding: 2,
          gap: 2,
          // Outranks the group's square inner corners and joined borders.
          "&& .MuiToggleButtonGroup-grouped": { border: 0, margin: 0, borderRadius: 6 },
        }),
      },
    },
    MuiToggleButton: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          gap: 6,
          border: 0,
          borderRadius: 6,
          color: theme.vars.palette.text.secondary,
          "&:hover": { backgroundColor: theme.vars.palette.action.hover },
          "&.Mui-selected, &.Mui-selected:hover": {
            backgroundColor: theme.vars.palette.action.selected,
            color: theme.vars.palette.text.primary,
          },
          "&.Mui-focusVisible": focusRing(theme, -2),
        }),
        // 28px options in the 2px-padded tray: 32px, like the other controls.
        sizeSmall: {
          minHeight: 28,
          padding: "0 10px",
        },
      },
    },
    // Every menu row, sidebar flyouts included (they are MenuLists too).
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) => menuRow(theme),
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: "0.8125rem",
        },
        secondary: {
          fontSize: "0.75rem",
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          paddingTop: 4,
          paddingBottom: 4,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: "0.6875rem",
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 10,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: "1rem",
          fontWeight: 600,
          padding: "12px 20px",
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: "12px 20px",
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: "8px 20px 12px",
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          fontSize: "0.625rem",
        },
      },
    },
    MuiAppBar: {
      defaultProps: {
        color: "default",
      },
    },
  },
});

export default theme;
